import { Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import find from 'lodash-es/find';
import flatten from 'lodash-es/flatten';
import map from 'lodash-es/map';
import { Subject, Subscription } from 'rxjs';
import { Customer } from '@df/session/api/customer';
import { HistoryOrder, IHistoryOrderData, historyOrderFactory } from '@df/session/api/history/history-order';
import { OrderService } from '@df/session/api/order/order.service';
import { IReject, IResolve, RequestService } from '@df/api/request/request.service';
import { Status } from '@df/core/status';
import { NDfAccount } from '@df/account/df-account.config';

@Injectable({
    providedIn: 'root'
})
export class History {
    orders!: HistoryOrder[];

    protected LOADED?: number;

    protected status = new Status();

    protected historyUpdateSubject = new Subject<void>();

    /**
     * Max history items qty requested to load
     */
    private maxQtyRequested = 0;

    private moduleConfig = inject(NDfAccount.OPTIONS);

    constructor(
        protected request: RequestService,
        protected client: Client,
        protected app: App,
        private customer: Customer,
        private orderService: OrderService
    ) {
        this.register();
        this.request.subscribe(result => this.onRequestSuccess(result));
        this.app.event.success(event => {
            if (event.id === 'nexusCheckoutSubmit') {
                this.invalidate();
            }
        });
    }

    private register() {
        this.customer.register(
            {
                id: 'history',
                listener: 'orders',
                account: true
            },
            this
        );
    }

    subscribe(observer): Subscription {
        return this.historyUpdateSubject.subscribe(observer);
    }

    /**
     * Invalidate promise by setting as not loaded
     */
    protected invalidate() {
        this.LOADED = 0;
        this.maxQtyRequested = 0;
    }

    protected onRequestSuccess(result): Promise<any> | void {
        // invalidate promise if checkout / success page visited
        if (result && result.customer) {
            if (result.checkout || result.success) {
                this.invalidate();
            }
        }
    }

    byId(id): HistoryOrder | undefined {
        return find(this.orders, order => order.id === id);
    }

    byNumber(incrementId): HistoryOrder | undefined {
        return find(this.orders, (item: HistoryOrder) => item.data.number === incrementId);
    }

    load(reload: boolean = false, qty = Infinity): Promise<this> {
        if (this.maxQtyRequested < qty) {
            reload = true;
        }
        return new Promise<this>((resolve: IResolve, reject: IReject) => {
            const cb = () => {
                resolve(this);
            };

            if (!this.app.isPlatformBrowser || this.client.isPrerender || !reload) {
                cb();
            } else {
                this.sendLoadRequest(qty)
                    .then(() => {
                        if (this.maxQtyRequested < qty) {
                            this.maxQtyRequested = qty;
                        }
                        cb();
                    })
                    .catch(reject);
            }
        });
    }

    protected sendLoadRequest(qty = Infinity) {
        return this.request.send(this.status, () =>
            this.request.nexus.customer.orders(qty === Infinity ? {} : { pagination: { limit: qty } })
        );
    }

    setData(data: IHistoryOrderData[]) {
        if (!data) {
            this.maxQtyRequested = 0;
        }
        this.orders = data
            ? map(data, orderData => new historyOrderFactory.instance(orderData, this.customer, this.orderService)) || []
            : [];

        if (data) {
            this.LOADED = Date.now();
        }

        // load dependencies
        const productIds = map(flatten(map(data, orderData => orderData.items)), order => order.product_id);

        if (productIds.length) {
            const promise = this.app.resources.product.loadAllIds(productIds, this.moduleConfig.historyProductVerbosity || 2);
            if (promise) {
                this.status.promise = promise;
                promise.finally(() => this.historyUpdateSubject.next());
            } else {
                this.historyUpdateSubject.next();
            }
            return promise; // this thing is needed to let loader wait for products
        } else {
            this.status = new Status();
            this.historyUpdateSubject.next();
        }

        return undefined;
    }
}

/**
 * Register service in customer
 */
declare module '@df/session/api/customer' {
    interface Customer {
        history: History;
    }
}
